import { Lock, Skull } from 'lucide-react'
import { clsx } from 'clsx'
import { Badge } from '@/shared/components/ui/Badge'
import { Flag } from '@/shared/components/ui/Flag'
import { Section } from '@/shared/components/ui/Section'
import { Skeleton } from '@/shared/components/ui/Skeleton'
import { playsHomeAndAway, showsCountryFlag } from '@/shared/lib/teamType'
import { errorMessage } from '@/shared/i18n/errorMessage'
import { useTranslation } from '@/shared/i18n/useTranslation'
import type { TranslateFn } from '@/shared/i18n/useTranslation'
import type { TranslationKey } from '@/shared/i18n/types'
import { useSurvivorAvailableTeams, useSubmitSurvivorPick } from '../queries'
import { PickResultBadge } from './PickResultBadge'
import type { SurvivorAvailableTeam, SurvivorPickHistoryEntry } from '../types'
import styles from './SurvivorPickPanel.module.css'

type Props = {
  gameId:         string
  gameweekId:     string
  gameweekNumber: number
  canPick:        boolean
  isCurrentGw:    boolean
  isAlive:        boolean
  isGameFinished: boolean
  myPicks:        SurvivorPickHistoryEntry[]
}

export function SurvivorPickPanel(props: Props) {
  const { t } = useTranslation()
  const myPickForGw = props.myPicks.find((p) => p.gameweekId === props.gameweekId)
  const availableTeamsQuery = useSurvivorAvailableTeams(
    props.gameId,
    props.canPick ? props.gameweekId : undefined,
  )
  const submit = useSubmitSurvivorPick(props.gameId)

  const roundLabel = t('survivor.round', { number: props.gameweekNumber })

  // ── Locked states ──────────────────────────────────────────────────────────
  // Every one of these is a round the caller cannot pick for, and every one of them shows the
  // pick they made in it when there is one. That last part is what makes the round browser
  // worth having: an eliminated player walking back through the game used to get nothing but
  // "you were eliminated" on every round, including the ones they played and won.

  if (!props.canPick) {
    return (
      <Section label={roundLabel}>
        <LockBanner
          icon={lockedIcon(props, myPickForGw)}
          text={t(lockedMessageKey(props, myPickForGw))}
        />
        {myPickForGw && <MyChoiceLine pick={myPickForGw} t={t} />}
      </Section>
    )
  }

  // ── Picking allowed ────────────────────────────────────────────────────────
  // The current pick sorts first rather than vanishing — the backend returns it
  // as an available, re-selectable option.
  const teams = [...(availableTeamsQuery.data ?? [])].sort((a, b) => {
    const aMine = a.teamId === myPickForGw?.teamId ? 0 : 1
    const bMine = b.teamId === myPickForGw?.teamId ? 0 : 1
    return aMine - bMine
  })

  const isLoading = availableTeamsQuery.isPending
  // Every branch of this used to end in `err.detail` — Portuguese server prose
  // rendered straight to the player. errorMessage() maps the code instead.
  const errorMsg = submit.error ? errorMessage(submit.error, t) : ''

  return (
    <Section label={t('survivor.pick.heading')} meta={roundLabel}>
      {isLoading && (
        <div className={styles.skeleton}>
          <Skeleton height="52px" />
          <Skeleton height="52px" />
          <Skeleton height="52px" />
        </div>
      )}

      {!isLoading && teams.length === 0 && (
        <p className={styles.emptyText}>{t('survivor.pick.exhausted')}</p>
      )}

      {teams.map((team) => {
        const isMine = myPickForGw?.teamId === team.teamId
        return (
          <button
            key={team.teamId}
            type="button"
            disabled={submit.isPending}
            className={clsx(styles.teamRow, isMine && styles.isPicked)}
            onClick={() => submit.mutate({ gameweekId: props.gameweekId, teamId: team.teamId })}
            aria-label={pickLabel(team, t)}
            aria-pressed={isMine}
          >
            {showsCountryFlag(team.teamType) ? (
              <Flag code={team.code} title={team.name} size={26} />
            ) : (
              <span className={styles.codeChip}>{team.code}</span>
            )}

            <span className={styles.teamText}>
              <span className={styles.teamName}>{team.name}</span>
              <span className={styles.teamMeta}>
                {team.code} · {t('fixture.vs')} {team.opponentCode}
                {playsHomeAndAway(team.teamType) && (
                  <> · {team.isHome ? t('survivor.pick.home') : t('survivor.pick.away')}</>
                )}
              </span>
            </span>

            {isMine && (
              <Badge tone="live" variant="solid">{t('survivor.pick.selected')}</Badge>
            )}
          </button>
        )
      })}

      {errorMsg && <p className={styles.errorMsg} role="alert">{errorMsg}</p>}
    </Section>
  )
}

/**
 * Why this round cannot be picked, in the order that reads best to the player.
 *
 * A pick they actually made outranks their game-level state: on a round they played, "your
 * picks for this round are closed" plus the pick itself says more than "you were eliminated",
 * which they already know and which is not what that round was about.
 */
function lockedMessageKey(props: Props, myPick: SurvivorPickHistoryEntry | undefined): TranslationKey {
  if (myPick)              return 'survivor.pick.closed'
  if (props.isGameFinished) return 'survivor.pick.gameOver'
  if (!props.isAlive)       return 'survivor.pick.eliminatedBanner'
  if (!props.isCurrentGw)   return 'survivor.pick.currentOnly'
  return 'survivor.pick.closed'
}

function lockedIcon(props: Props, myPick: SurvivorPickHistoryEntry | undefined) {
  return !myPick && !props.isAlive && !props.isGameFinished
    ? <Skull size={18} />
    : <Lock size={18} />
}

function LockBanner({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className={styles.lockBanner}>
      <span className={styles.lockIcon} aria-hidden="true">{icon}</span>
      <span>{text}</span>
    </div>
  )
}

function MyChoiceLine({ pick, t }: { pick: SurvivorPickHistoryEntry; t: TranslateFn }) {
  // A FORFEIT row has no team — it records that the round went by without a pick, which is
  // worth showing rather than hiding, so the round reads the same as it does in the history.
  if (!pick.teamId) {
    return (
      <p className={styles.myChoice}>
        <span>{t('survivor.pick.missed')}</span>
        {' '}
        <PickResultBadge result={pick.result} />
      </p>
    )
  }

  return (
    <p className={styles.myChoice}>
      <span>{t('survivor.pick.yoursIs', { team: `${pick.teamCode} — ${pick.teamName}` })}</span>
      {' '}
      <PickResultBadge result={pick.result} />
    </p>
  )
}

/** The visual venue chip is lost to a screen reader, so it goes in the label too. */
function pickLabel(team: SurvivorAvailableTeam, t: TranslateFn): string {
  const key = !playsHomeAndAway(team.teamType)
    ? 'survivor.pick.a11y.neutral'
    : team.isHome
      ? 'survivor.pick.a11y.home'
      : 'survivor.pick.a11y.away'

  return t(key, { team: team.name, opponent: team.opponentName ?? team.opponentCode ?? "" })
}
